import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Button, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow,
  Text, Title,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import {
  listAdminUsers, createAdminUser, updateAdminUser, deleteAdminUser, getLicense, getPermissionsCatalog,
  getRemoteSync, triggerRemoteSync,
  SeatLimitError, LicenseExpiredError, UsernameTakenError, LastOwnerError, EmailTakenError, EmailInvalidError,
} from '../api/admin';
import type { AdminUser, Entitlement, PermissionsCatalog, RemoteSyncStatus } from '../api/admin';
import RemoteSyncStrip from '../components/RemoteSyncStrip';
import AccessDenied from '../components/AccessDenied';
import ActivityPanel from '../components/ActivityPanel';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import Header from '../components/Header';
import SeatBanner from '../components/SeatBanner';
import TableSkeleton from '../components/TableSkeleton';
import UserFormDialog, { type UserFormValue } from '../components/UserFormDialog';
import { t } from '../lib/i18n';
import { licenseAllowsWrites } from '../lib/roles';
import type { User } from '../api/types';

export default function AdminUsers() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  // PermUserSeed is owner-only by default. Non-owners get the standard
  // AccessDenied surface instead of a 403 wall.
  if (user.role !== 'owner') {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4"><AccessDenied user={user} requiredRoleLabel={t.roles.owner} /></main>
      </div>
    );
  }
  return <AdminUsersInner user={user} />;
}

function AdminUsersInner({ user }: { user: User }) {
  const [rows, setRows] = useState<AdminUser[] | null>(null);
  const [ent, setEnt] = useState<Entitlement | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; row: AdminUser }
    | null
  >(null);
  const [dialogErr, setDialogErr] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<PermissionsCatalog | null>(null);
  const [sync, setSync] = useState<RemoteSyncStatus | null>(null);

  function load() {
    setErr(null);
    Promise.all([listAdminUsers(), getLicense()])
      .then(([list, lic]) => {
        setRows(list);
        setEnt(lic);
      })
      .catch((e: Error) => setErr(e.message ?? String(e)));
    // Remote-access sync status is optional: a failure just hides the strip.
    getRemoteSync().then(setSync).catch(() => setSync(null));
  }

  async function syncNow() {
    try {
      setSync(await triggerRemoteSync());
    } catch (e) {
      setErr(humanizeError(e));
    }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Catalog is small + immutable per build; fetch once and reuse for every
    // dialog open. Failure is non-fatal — the dialog hides the permissions
    // section when catalog is null.
    getPermissionsCatalog().then(setCatalog).catch(() => { /* ignore */ });
  }, []);

  const atLimit = ent ? ent.seats_used >= ent.seats_allowed : false;
  // Seat-limit gating stays keyed off the admin Entitlement (ent, /api/admin/
  // license — SeatBanner's data source); the WRITE-block keys off /api/me's
  // user.license via licenseAllowsWrites instead, so it also catches
  // cannot_validate/misconfigured/clock_skew, not just a plain 'expired'.
  const licenseBlocked = !licenseAllowsWrites(user);
  // The disabled-button tooltip must name the ACTUAL reason, not always
  // "Licencia vencida" (t.admin.licenseExpired) — that copy is only true
  // when the status really is 'expired'. Reuses the same distinct per-status
  // copy LicenseBanner shows elsewhere (lib/i18n.ts licenseBanner.*) so the
  // two surfaces never disagree; a status-neutral fallback covers any status
  // not explicitly named here.
  const licenseStatus = user.license?.status;
  const licenseBlockedReason =
    licenseStatus === 'misconfigured'   ? t.licenseBanner.misconfigured :
    licenseStatus === 'clock_skew'      ? t.licenseBanner.clockSkew :
    licenseStatus === 'cannot_validate' ? t.licenseBanner.cannotValidate :
    licenseStatus === 'expired'         ? t.admin.licenseExpired :
    t.admin.licenseWriteBlocked;

  async function handleCreate(v: UserFormValue) {
    setDialogErr(null);
    try {
      await createAdminUser({
        username: v.username,
        password: v.password ?? '',
        role: v.role,
        sid_assigned: v.sid_assigned,
        permissions: v.permissions,
        email: v.email,
      });
      setDialog(null);
      load();
    } catch (e) {
      setDialogErr(humanizeError(e));
    }
  }

  async function handleEdit(orig: AdminUser, v: UserFormValue) {
    setDialogErr(null);
    try {
      await updateAdminUser(orig.username, {
        role: v.role,
        sid_assigned: v.sid_assigned,
        password: v.password,
        permissions: v.permissions,
        email: v.email,
      });
      setDialog(null);
      load();
    } catch (e) {
      setDialogErr(humanizeError(e));
    }
  }

  async function handleDelete(row: AdminUser) {
    if (!confirm(t.admin.confirmDelete.replace('{username}', row.username))) return;
    try {
      await deleteAdminUser(row.username);
      load();
    } catch (e) {
      setErr(humanizeError(e));
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <Title>{t.admin.pageTitle}</Title>
            <Text className="text-tremor-content text-sm">{t.admin.pageSubtitle}</Text>
          </div>
          <Button
            disabled={atLimit || licenseBlocked}
            onClick={() => { setDialogErr(null); setDialog({ mode: 'create' }); }}
            title={atLimit ? t.admin.addUserDisabledLimit : licenseBlocked ? licenseBlockedReason : undefined}
          >
            ＋ {t.admin.addUser}
          </Button>
        </div>

        <SeatBanner entitlement={ent} />

        <RemoteSyncStrip status={sync} onSyncNow={syncNow} />

        <ActivityPanel />

        {err && <ErrorRetry message={err} onRetry={load} />}

        {!err && rows === null && <TableSkeleton rows={6} cols={6} />}
        {!err && rows !== null && rows.length === 0 && (
          <EmptyState message="—" />
        )}
        {!err && rows !== null && rows.length > 0 && (
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.admin.columns.username}</TableHeaderCell>
                  <TableHeaderCell>{t.admin.columns.role}</TableHeaderCell>
                  <TableHeaderCell>{t.admin.columns.sid}</TableHeaderCell>
                  <TableHeaderCell>{t.admin.columns.email}</TableHeaderCell>
                  <TableHeaderCell>{t.admin.columns.createdAt}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.admin.columns.actions}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.username}>
                    <TableCell className="font-medium">{r.username}</TableCell>
                    <TableCell>{t.roles[r.role]}</TableCell>
                    <TableCell>{r.sid_assigned ?? '—'}</TableCell>
                    <TableCell>
                      {r.email || '—'}
                      {(() => {
                        const rej = r.email ? sync?.rejected.find((x) => x.email === r.email) : undefined;
                        if (!rej) return null;
                        const reasons = t.admin.remote.rejected as Record<string, string>;
                        return <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">{reasons[rej.reason] ?? rej.reason}</span>;
                      })()}
                    </TableCell>
                    <TableCell>{r.created_at?.slice(0, 10) ?? '—'}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="xs" variant="light" onClick={() => { setDialogErr(null); setDialog({ mode: 'edit', row: r }); }}>
                        {t.admin.actions.edit}
                      </Button>
                      <Button size="xs" variant="light" color="red" onClick={() => handleDelete(r)}>
                        {t.admin.actions.delete}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </main>

      {dialog?.mode === 'create' && (
        <UserFormDialog
          mode="create"
          catalog={catalog}
          onSubmit={(v) => handleCreate(v)}
          onCancel={() => setDialog(null)}
          errorText={dialogErr}
        />
      )}
      {dialog?.mode === 'edit' && (
        <UserFormDialog
          mode="edit"
          initial={dialog.row}
          catalog={catalog}
          onSubmit={(v) => handleEdit(dialog.row, v)}
          onCancel={() => setDialog(null)}
          errorText={dialogErr}
        />
      )}
    </div>
  );
}

function humanizeError(e: unknown): string {
  if (e instanceof SeatLimitError)     return t.admin.errors.seatLimit;
  if (e instanceof LicenseExpiredError) return t.admin.errors.licenseExpired;
  if (e instanceof UsernameTakenError)  return t.admin.errors.usernameTaken;
  if (e instanceof LastOwnerError)      return t.admin.errors.lastOwner;
  if (e instanceof EmailTakenError)     return t.admin.errors.emailTaken;
  if (e instanceof EmailInvalidError)   return t.admin.errors.emailInvalid;
  if (e instanceof Error)                return e.message;
  return t.admin.errors.generic;
}
